import React from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { default as SheetPortal } from 'browser/components/atomic-elements/atoms/sheet/sheet-portal'
import { PreviewCard } from 'browser/components/json-elements/atoms/preview-card/preview-card'

interface IPreviewDetailCell {
  label?: string
  value?: string
}

/**
 * @uiComponent
 *
 * A summary card that opens the caller's `children` uiSchema in a slide-in sheet for editing a large
 * or nested value. The children render through the normal renderer (same `value`/frame binding as
 * anywhere else), so the sheet's form is fully described in JSON; edits write through immediately (no
 * separate save — "Done" just closes, and the surrounding panel/detail owns persistence).
 *
 * The trigger is fully caller-configurable via the `previewCard` slot — any uiSchema node (typically
 * `ui:previewCard`). It's rendered in a child frame (so its `mapStateToProps`/value paths resolve in
 * this component's frame) and given the open handler via the renderer's `defaultProps` — the same way
 * `ui:conditional` forwards props to its rendered block. If `previewCard` is omitted, a default
 * `ui:previewCard` is built from the `title`/`subtitle`/`detail` props.
 *
 * Example:
 * {
 *   "type": "ui:embeddedSheet",
 *   "sheetTitle": "Entity Spec",
 *   "previewCard": {
 *     "type": "ui:previewCard",
 *     "icon": "edit",
 *     "mapStateToProps": { "title": "<expr>", "subtitle": "<expr>" }
 *   },
 *   "children": [ ... the value's form uiSchema ... ]
 * }
 */
export interface IEmbeddedSheetProps extends IBaseProps {
  frames?: FramesManager
  /** Fallback preview-card fields when no `previewCard` slot is supplied. */
  title?: string
  subtitle?: string
  detail?: IPreviewDetailCell[]
  /** Sheet header text (distinct from the card title). */
  sheetTitle?: string
  /** Sheet size passed through to the underlying Sheet (e.g. "sm", "md"). */
  size?: string
}

interface IEmbeddedSheetState {
  isOpen: boolean
}

export class EmbeddedSheet extends React.Component<IEmbeddedSheetProps, IEmbeddedSheetState> {
  public state: IEmbeddedSheetState = { isOpen: false }

  private open = () => this.setState({ isOpen: true })
  private close = () => this.setState({ isOpen: false })

  public render() {
    const { children, className, style, sheetTitle, size } = this.props
    return (
      <div className={className} style={style}>
        {this.renderTrigger()}
        <SheetPortal isOpen={this.state.isOpen} size={size || 'sm'} footer={this.renderFooter()}>
          <div className="c-sheet-body--padded">
            {sheetTitle && <h4 className="c-sheet-title u-bumperBottom">{sheetTitle}</h4>}
            {children}
          </div>
        </SheetPortal>
      </div>
    )
  }

  /**
   * Render the caller's `previewCard` slot in a child frame and forward `onClick: open` via the
   * renderer's defaultProps (same mechanism as ui:conditional). Falls back to a default PreviewCard.
   */
  private renderTrigger() {
    const { frames, title, subtitle, detail } = this.props
    const previewCard = frames?.getContext('uiSchema')?.previewCard
    if (previewCard && frames) {
      const renderer = frames.getContext('renderer')
      const uiSchemaPath = frames.getContext('uiSchemaPath').concat(['previewCard'])
      const cardFrames = renderer.createChildFrame(frames, { uiSchema: previewCard, uiSchemaPath })
      return renderer.createElementFromFrame(cardFrames, { onClick: this.open })
    }
    return (
      <PreviewCard title={title} subtitle={subtitle} detail={detail} icon="edit" onClick={this.open} />
    )
  }

  private renderFooter() {
    // Only a "Done" button: edits write through live (no draft), so a "Cancel" that can't revert
    // would mislead. Omitting onCancelButtonClick hides the cancel button (see Footer.renderCancelButton).
    return <Footer isVisible={true} primaryButtonText="Done" onPrimaryButtonClick={this.close} />
  }
}
